'use client';

import { useId } from 'react';

import { ChevronRight, CircleAlert, Inbox, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';
import type { SupportTicket } from '@/lib/posthog-support/types';

import {
  getPostHogSupportContentWidth,
  getPostHogSupportHorizontalPadding,
} from './posthog-support-layout';
import {
  getSupportStatusPresentation,
  getTicketDisplayTitle,
} from './posthog-support-state';

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;

function formatTicketDate(value: string, locale: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function statusClassName(
  tone: ReturnType<typeof getSupportStatusPresentation>['tone']
) {
  if (tone === 'active') return 'bg-primary/10 text-primary';
  if (tone === 'attention') return 'bg-accent text-accent-foreground';
  if (tone === 'resolved') return 'bg-muted text-muted-foreground';
  return 'bg-muted/60 text-muted-foreground';
}

export function PostHogSupportRequests({
  compact,
  copy,
  error,
  hasMore,
  isLoadingMore,
  isRetrying,
  locale,
  onLoadMore,
  onNewRequest,
  onRetry,
  onSelect,
  selectingTicketId,
  tickets,
}: {
  compact: boolean;
  copy: SupportCopy;
  error: string | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  isRetrying: boolean;
  locale: string;
  onLoadMore: () => void | Promise<void>;
  onNewRequest: () => void;
  onRetry: () => void | Promise<void>;
  onSelect: (ticketId: string) => void | Promise<void>;
  selectingTicketId: string | null;
  tickets: SupportTicket[];
}) {
  const headingId = useId();

  const heading = (
    <h2
      id={headingId}
      className="sr-only"
      data-support-view-heading
      tabIndex={-1}
    >
      {copy.myRequests}
    </h2>
  );

  if (error && tickets.length === 0) {
    return (
      <section
        className="flex min-h-0 flex-1 flex-col"
        aria-labelledby={headingId}
      >
        {heading}
        <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-10">
          <div className="max-w-xs text-center">
            <span className="bg-muted text-muted-foreground mx-auto flex size-10 items-center justify-center rounded-xl">
              <CircleAlert className="size-5" aria-hidden />
            </span>
            <p
              role="alert"
              className="text-destructive mt-3 text-sm text-pretty"
            >
              {error}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-4 min-h-11 sm:min-h-8"
              aria-disabled={isRetrying}
              aria-busy={isRetrying}
              onClick={() => {
                if (isRetrying) return;
                void onRetry();
              }}
            >
              {isRetrying ? (
                <span aria-hidden="true">
                  <Loader />
                </span>
              ) : null}
              {copy.retry}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (tickets.length === 0) {
    return (
      <section
        className="flex min-h-0 flex-1 flex-col"
        aria-labelledby={headingId}
      >
        {heading}
        <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-10">
          <div className="max-w-xs text-center">
            <span className="bg-muted text-muted-foreground mx-auto flex size-10 items-center justify-center rounded-xl">
              <Inbox className="size-5" aria-hidden />
            </span>
            <p className="mt-3 text-sm font-medium text-balance">
              {copy.emptyRequests}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-4 min-h-11 sm:min-h-8"
              onClick={onNewRequest}
            >
              <Plus className="size-4" aria-hidden />
              {copy.newRequest}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="flex min-h-0 flex-1 flex-col"
      aria-labelledby={headingId}
    >
      {heading}
      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto',
          getPostHogSupportHorizontalPadding(compact),
          compact ? 'py-3' : 'py-6 sm:py-8'
        )}
      >
        <ul
          className={cn(
            'divide-border/35 border-border/35 divide-y overflow-hidden rounded-xl border',
            getPostHogSupportContentWidth(compact)
          )}
        >
          {tickets.map((ticket) => {
            const status = getSupportStatusPresentation(ticket.status);
            const statusLabel = copy[status.copyKey];
            const formattedDate = formatTicketDate(ticket.updatedAt, locale);
            const isSelecting = selectingTicketId === ticket.id;

            return (
              <li key={ticket.id}>
                <button
                  type="button"
                  className="hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:ring-ring flex min-h-20 w-full items-center gap-3 px-4 py-3 text-start outline-none focus-visible:ring-2 focus-visible:ring-inset aria-disabled:cursor-wait aria-disabled:opacity-60"
                  aria-disabled={selectingTicketId !== null}
                  aria-busy={isSelecting}
                  onClick={() => {
                    if (selectingTicketId !== null) return;
                    void onSelect(ticket.id);
                  }}
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {getTicketDisplayTitle(ticket)}
                      </span>
                      {ticket.unreadCount > 0 ? (
                        <span className="bg-primary text-primary-foreground flex min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums">
                          <span className="sr-only">{copy.unread}</span>
                          {ticket.unreadCount}
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-2">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          statusClassName(status.tone)
                        )}
                      >
                        {statusLabel}
                      </span>
                      {formattedDate ? (
                        <time
                          dateTime={ticket.updatedAt}
                          className="text-muted-foreground text-xs"
                        >
                          {formattedDate}
                        </time>
                      ) : null}
                    </span>
                  </span>
                  {isSelecting ? (
                    <span aria-hidden="true">
                      <Loader />
                    </span>
                  ) : (
                    <ChevronRight
                      className="text-muted-foreground size-4 shrink-0 rtl:rotate-180"
                      aria-hidden
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {hasMore ? (
          <div className="mt-3 flex justify-center">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="min-h-11 aria-disabled:cursor-wait aria-disabled:opacity-60 sm:min-h-8"
              aria-disabled={isLoadingMore}
              aria-busy={isLoadingMore}
              onClick={() => {
                if (isLoadingMore) return;
                void onLoadMore();
              }}
            >
              {isLoadingMore ? (
                <>
                  <span aria-hidden="true">
                    <Loader />
                  </span>
                  {copy.loading}
                </>
              ) : (
                copy.loadMore
              )}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
